import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardContent, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { 
  FileText, 
  AlertTriangle, 
  Users, 
  DollarSign, 
  Clock, 
  CheckCircle,
  Phone,
  Mail,
  Building
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import proApiService from '../../services/proApiService';

const InsuranceDashboard = () => {
  const { user, hasPermission } = useAuth();
  const [disputes, setDisputes] = useState([]);
  const [projects, setProjects] = useState([]);
  const [communications, setCommunications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalDisputes: 0,
    activeDisputes: 0,
    resolvedDisputes: 0,
    totalDisputedAmount: 0
  });

  useEffect(() => {
    loadInsuranceData();
  }, []);

  const loadInsuranceData = async () => {
    try {
      setLoading(true);
      
      // Load disputes
      const disputesData = await proApiService.getDisputes();
      setDisputes(disputesData);
      
      // Load projects with insurance info
      const projectsData = await proApiService.getProjects();
      const insuranceProjects = projectsData.filter(p => p.insurance_company);
      setProjects(insuranceProjects);
      
      // Load insurance-related communications
      const commsData = await proApiService.getCommunications({ 
        communication_type: 'proposal' 
      });
      setCommunications(commsData);
      
      // Load dispute statistics
      const disputeStats = await proApiService.getDisputesDashboard();
      setStats(disputeStats);
      
    } catch (error) {
      console.error('Error loading insurance data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-red-100 text-red-800';
      case 'in_review': return 'bg-yellow-100 text-yellow-800';
      case 'escalated': return 'bg-orange-100 text-orange-800';
      case 'resolved': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-32 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Insurance Module</h1>
          <p className="text-gray-600">Manage insurance claims, disputes, and communications</p>
        </div>
        {hasPermission('disputes.create') && (
          <Button className="bg-blue-600 hover:bg-blue-700">
            <AlertTriangle className="w-4 h-4 mr-2" />
            File New Dispute
          </Button>
        )}
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Disputes</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalDisputes}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Active Disputes</p>
                <p className="text-2xl font-bold text-orange-600">{stats.activeDisputes}</p>
              </div>
              <Clock className="w-8 h-8 text-orange-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Resolved Disputes</p>
                <p className="text-2xl font-bold text-green-600">{stats.resolvedDisputes}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Amount in Dispute</p>
                <p className="text-2xl font-bold text-red-600">
                  {formatCurrency(stats.totalDisputedAmount)}
                </p>
              </div>
              <DollarSign className="w-8 h-8 text-red-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs defaultValue="disputes" className="space-y-6">
        <TabsList>
          <TabsTrigger value="disputes">Active Disputes</TabsTrigger>
          <TabsTrigger value="projects">Insurance Projects</TabsTrigger>
          <TabsTrigger value="communications">Communications</TabsTrigger>
          <TabsTrigger value="companies">Insurance Companies</TabsTrigger>
        </TabsList>

        {/* Disputes Tab */}
        <TabsContent value="disputes">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <AlertTriangle className="w-5 h-5 mr-2" />
                Insurance Disputes
              </CardTitle>
            </CardHeader>
            <CardContent>
              {disputes.length > 0 ? (
                <div className="space-y-4">
                  {disputes.map((dispute) => (
                    <div key={dispute.id} className="border rounded-lg p-4 hover:bg-gray-50">
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900">{dispute.dispute_type}</h3>
                          <p className="text-sm text-gray-600">{dispute.description}</p>
                        </div>
                        <Badge className={getStatusColor(dispute.status)}>
                          {dispute.status.replace('_', ' ').toUpperCase()}
                        </Badge>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3 text-sm">
                        <div>
                          <span className="text-gray-500">Insurance Company:</span>
                          <p className="font-medium">{dispute.insurance_company || 'Not specified'}</p>
                        </div>
                        <div>
                          <span className="text-gray-500">Disputed Amount:</span>
                          <p className="font-medium text-red-600">
                            {dispute.disputed_amount ? formatCurrency(dispute.disputed_amount) : 'Not specified'}
                          </p>
                        </div>
                        <div>
                          <span className="text-gray-500">Created:</span>
                          <p className="font-medium">
                            {new Date(dispute.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>

                      <div className="flex justify-end mt-4 space-x-2">
                        <Button variant="outline" size="sm">View Details</Button>
                        <Button variant="outline" size="sm">Update Status</Button>
                        <Button variant="outline" size="sm">Add Document</Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <AlertTriangle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No active disputes found</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Insurance Projects Tab */}
        <TabsContent value="projects">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Building className="w-5 h-5 mr-2" />
                Insurance Projects
              </CardTitle>
            </CardHeader>
            <CardContent>
              {projects.length > 0 ? (
                <div className="space-y-4">
                  {projects.map((project) => (
                    <div key={project.id} className="border rounded-lg p-4 hover:bg-gray-50">
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900">{project.name}</h3>
                          <p className="text-sm text-gray-600">{project.address}</p>
                        </div>
                        <Badge className={getStatusColor(project.status)}>
                          {project.status.replace('_', ' ').toUpperCase()}
                        </Badge>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-3 text-sm">
                        <div>
                          <span className="text-gray-500">Insurance Company:</span>
                          <p className="font-medium">{project.insurance_company}</p>
                        </div>
                        <div>
                          <span className="text-gray-500">Claim Number:</span>
                          <p className="font-medium">{project.claim_number || 'Pending'}</p>
                        </div>
                        <div>
                          <span className="text-gray-500">Adjuster:</span>
                          <p className="font-medium">{project.adjuster_name || 'Not assigned'}</p>
                        </div>
                        <div>
                          <span className="text-gray-500">Estimate:</span>
                          <p className="font-medium text-green-600">
                            {project.final_estimate ? formatCurrency(project.final_estimate) : 'Pending'}
                          </p>
                        </div>
                      </div>

                      <div className="flex justify-end mt-4 space-x-2">
                        <Button variant="outline" size="sm">
                          <Mail className="w-4 h-4 mr-1" />
                          Send Update
                        </Button>
                        <Button variant="outline" size="sm">
                          <FileText className="w-4 h-4 mr-1" />
                          Generate Report
                        </Button>
                        <Button variant="outline" size="sm">View Project</Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Building className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No insurance projects found</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Communications Tab */}
        <TabsContent value="communications">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Mail className="w-5 h-5 mr-2" />
                Insurance Communications
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {communications.map((comm) => (
                  <div key={comm.id} className="border rounded-lg p-4 hover:bg-gray-50">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900">{comm.subject}</h3>
                        <p className="text-sm text-gray-600">To: {comm.recipients.join(', ')}</p>
                        <p className="text-sm text-gray-500 mt-1">
                          {new Date(comm.created_at).toLocaleDateString()} • {comm.communication_type}
                        </p>
                      </div>
                      <Badge className={comm.delivery_status === 'sent' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                        {comm.delivery_status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Insurance Companies Tab */}
        <TabsContent value="companies">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Users className="w-5 h-5 mr-2" />
                Insurance Company Contacts
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Mock insurance company data */}
                {[
                  { name: 'State Farm', contact: 'John Smith', email: 'j.smith@statefarm.com', phone: '(555) 123-4567' },
                  { name: 'Allstate', contact: 'Sarah Johnson', email: 's.johnson@allstate.com', phone: '(555) 234-5678' },
                  { name: 'Farmers Insurance', contact: 'Mike Davis', email: 'm.davis@farmers.com', phone: '(555) 345-6789' },
                ].map((company, index) => (
                  <div key={index} className="border rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 mb-2">{company.name}</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center text-gray-600">
                        <Users className="w-4 h-4 mr-2" />
                        {company.contact}
                      </div>
                      <div className="flex items-center text-gray-600">
                        <Mail className="w-4 h-4 mr-2" />
                        {company.email}
                      </div>
                      <div className="flex items-center text-gray-600">
                        <Phone className="w-4 h-4 mr-2" />
                        {company.phone}
                      </div>
                    </div>
                    <Button variant="outline" size="sm" className="w-full mt-3">
                      Contact Adjuster
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default InsuranceDashboard;